// INC2-CONTACT: one connection's drawer (integrations design §2.10, F4). Tabs: Health (state,
// identity, checks and token times; Check · Refresh · Reconnect · Disconnect), Grants (who may
// use it at Read · Act · Manage with families and limits; Grant access opens a modal; revoke is a
// hover icon), Activity (acts through it, from the outbox), Dead letters (Retry · Cancel), and
// Sync (none yet: subscriptions and pulls arrive with the provider adapters). Never sample data.
import {
  Badge,
  Button,
  EmptyState,
  InfoTip,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  toast,
  ViewTabs,
  WorkerBadge,
} from "@companyos/ui";
import {
  type UseQueryResult,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { BanIcon, KeyRoundIcon, RefreshCwIcon, RotateCcwIcon, SendIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import type { Api } from "../../api/client";
import { outboxKey, outboxSummary } from "../agents/outbox-screen";
import { ConfirmRowAction } from "../confirm-row-action";
import { formatDateTime } from "../format";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { problemText } from "../settings/problem-text";
import { RowAction, RowActions } from "../settings/row-actions";
import { HEALTH, healthKey, NEEDS_RECONNECT } from "../subscriptions/copy";
import type { Connection } from "../subscriptions/queries";
import { useFeature } from "../use-feature";
import { useConnectionActions } from "./actions";
import { GrantDialog } from "./grant-dialog";
import {
  familiesText,
  type Grant,
  LEVEL_INFO,
  LEVEL_LABEL,
  limitsText,
  PROVIDER_NAME,
  type useGrantees,
} from "./grants";

export const DRAWER_TABS = ["health", "grants", "activity", "dead", "sync"] as const;
export type DrawerTab = (typeof DRAWER_TABS)[number];
const TAB_LABEL: Record<DrawerTab, string> = {
  health: "Health",
  grants: "Grants",
  activity: "Activity",
  dead: "Dead letters",
  sync: "Sync",
};

export function ConnectionDrawer({
  api,
  co,
  slug,
  connection: c,
  grants,
  grantsState,
  names,
  tab,
  onTab,
  onClose,
}: {
  api: Api;
  co: string;
  slug: string;
  connection: Connection;
  grants: Grant[];
  grantsState: UseQueryResult<unknown>;
  names: ReturnType<typeof useGrantees>;
  tab: DrawerTab;
  onTab: (t: DrawerTab) => void;
  onClose: () => void;
}) {
  return (
    <Sheet open onOpenChange={(o) => (o ? null : onClose())}>
      <SheetContent data-slot="connection-drawer" className="gap-3 overflow-y-auto sm:max-w-lg">
        <SheetHeader>
          <SheetTitle>{c.label}</SheetTitle>
          <SheetDescription>
            {PROVIDER_NAME[c.provider] ?? c.provider} ·{" "}
            {c.scope === "company" ? "Company" : "Personal"}
          </SheetDescription>
        </SheetHeader>
        <div className="px-4">
          <ViewTabs
            aria-label="Connection"
            value={tab}
            onValueChange={(v) => onTab(v as DrawerTab)}
            tabs={DRAWER_TABS.map((t) => ({
              value: t,
              label: TAB_LABEL[t],
              ...(t === "grants" ? { count: grants.length } : {}),
            }))}
          />
        </div>
        <div role="tabpanel" aria-label={TAB_LABEL[tab]} className="flex flex-col gap-3 px-4 pb-4">
          {tab === "health" ? (
            <HealthTab api={api} co={co} slug={slug} c={c} />
          ) : tab === "grants" ? (
            grantsState.isPending ? (
              <Loading label="Loading grants" rows={2} />
            ) : grantsState.isError ? (
              <LoadError what="Grants" error={grantsState.error} />
            ) : (
              <GrantsTab api={api} co={co} slug={slug} c={c} grants={grants} names={names} />
            )
          ) : tab === "activity" ? (
            <ActsTab api={api} co={co} c={c} dead={false} />
          ) : tab === "dead" ? (
            <ActsTab api={api} co={co} c={c} dead />
          ) : (
            <EmptyState
              variant="compact"
              icon={RefreshCwIcon}
              title="No sync"
              description="This connection reads live; nothing is synced yet."
            />
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-baseline justify-between gap-4 text-[13px]">
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="min-w-0 truncate text-right">{children}</dd>
    </div>
  );
}

function HealthTab({ api, co, slug, c }: { api: Api; co: string; slug: string; c: Connection }) {
  const act = useConnectionActions(api, co, slug);
  const key = healthKey(c);
  const h = HEALTH[key] ?? { label: c.state, tone: "neutral" as const, sentence: "" };
  const oauth = c.provider !== "api_key";
  const signIn = oauth && NEEDS_RECONNECT.has(c.state);
  return (
    <div className="flex flex-col gap-3" data-slot="connection-health">
      <p className="flex items-center gap-2 text-[13px]">
        <Badge variant={h.tone}>{h.label}</Badge>
        <span className="text-muted-foreground">{h.sentence}</span>
      </p>
      <dl className="flex flex-col gap-1.5">
        <Fact label="Account">
          <span className="font-mono">{c.config?.host ?? c.identity_display ?? "—"}</span>
        </Fact>
        {c.key_last4 ? (
          <Fact label="Key">
            <span className="font-mono">••••{c.key_last4}</span>
          </Fact>
        ) : null}
        {c.tiers.length ? <Fact label="Access">{c.tiers.join(" · ")}</Fact> : null}
        <Fact label="Checked">
          {c.last_checked_at ? formatDateTime(c.last_checked_at) : "Never"}
        </Fact>
        <Fact label="Last worked">
          {c.last_verified_at ? formatDateTime(c.last_verified_at) : "Never"}
        </Fact>
        {c.token_expires_at ? (
          <Fact label="Token renews">{formatDateTime(c.token_expires_at)}</Fact>
        ) : null}
        {c.rate_limited_until ? (
          <Fact label="Slowed until">{formatDateTime(c.rate_limited_until)}</Fact>
        ) : null}
        <Fact label="Connected">{formatDateTime(c.created_at)}</Fact>
      </dl>
      <div className="flex flex-wrap gap-2">
        <Button
          size="sm"
          variant="secondary"
          disabled={act.check.isPending}
          onClick={() => act.check.mutate(c)}
        >
          Check
        </Button>
        {oauth && !signIn ? (
          <Button
            size="sm"
            variant="secondary"
            disabled={act.renew.isPending}
            onClick={() => act.renew.mutate(c)}
          >
            Refresh
          </Button>
        ) : null}
        {signIn ? (
          <Button
            size="sm"
            disabled={act.reconnect.isPending}
            onClick={() => act.reconnect.mutate(c)}
          >
            Reconnect
          </Button>
        ) : null}
      </div>
    </div>
  );
}

function GrantsTab({
  api,
  co,
  slug,
  c,
  grants,
  names,
}: {
  api: Api;
  co: string;
  slug: string;
  c: Connection;
  grants: Grant[];
  names: ReturnType<typeof useGrantees>;
}) {
  const act = useConnectionActions(api, co, slug);
  const [adding, setAdding] = useState(false);
  return (
    <div className="flex flex-col gap-3" data-slot="connection-grants">
      {grants.length === 0 ? (
        <EmptyState
          variant="compact"
          icon={KeyRoundIcon}
          title="No grants"
          description={
            c.scope === "person"
              ? "Only you use it. Grant a DE to let it read or act through it."
              : "Nobody uses it yet. Grant a person, DE, seat or team."
          }
        />
      ) : (
        <ul aria-label="Grants" className="flex flex-col">
          {grants.map((g) => {
            const who = names.of(g);
            const limits = limitsText(g.limits);
            return (
              <li
                key={g.id}
                data-grant={g.id}
                className="group/row flex min-h-9 items-center gap-2 border-b border-border py-1.5 text-[13px]"
              >
                {who.worker ? <WorkerBadge kind={who.worker} size="sm" /> : null}
                <span className="min-w-0 flex-1 truncate">
                  {who.name}
                  {g.grantee_kind === "seat" || g.grantee_kind === "team" ? (
                    <span className="text-muted-foreground"> · {g.grantee_kind}</span>
                  ) : null}
                </span>
                <Badge
                  variant={g.level === "manage" ? "accent" : "outline"}
                  title={LEVEL_INFO[g.level]}
                >
                  {LEVEL_LABEL[g.level]}
                </Badge>
                <span className="max-sm:hidden text-xs text-muted-foreground">
                  {familiesText(g.families)}
                </span>
                {limits ? <InfoTip label={`Limits of ${who.name}`}>{limits}</InfoTip> : null}
                <RowActions className="w-8">
                  {g.grantee_kind === "crew" ? (
                    <InfoTip label={`About ${who.name}'s access`}>Set in My agent.</InfoTip>
                  ) : (
                    <ConfirmRowAction
                      icon={BanIcon}
                      label={`Revoke ${who.name}`}
                      tip="Revoke"
                      title={`Revoke ${who.name}'s access?`}
                      description="This grant ends at once. Anything queued is checked again against the access that remains."
                      confirmLabel="Revoke"
                      disabled={act.revokeGrant.isPending}
                      onConfirm={() => act.revokeGrant.mutate(g)}
                    />
                  )}
                </RowActions>
              </li>
            );
          })}
        </ul>
      )}
      <div>
        <Button size="sm" onClick={() => setAdding(true)}>
          <KeyRoundIcon data-icon="inline-start" aria-hidden="true" />
          Grant access
        </Button>
      </div>
      <GrantDialog
        api={api}
        co={co}
        connection={c}
        names={names}
        open={adding}
        onOpenChange={setAdding}
      />
    </div>
  );
}

/** Acts through the connection (the outbox), or only its dead letters with Retry and Cancel. */
function ActsTab({ api, co, c, dead }: { api: Api; co: string; c: Connection; dead: boolean }) {
  const agents = useFeature("agents");
  const qc = useQueryClient();
  // Paged (Codex r2 #10): older dead letters stay reachable through "Load more"
  const q = useInfiniteQuery({
    queryKey: [...outboxKey(co), "connection", c.id, dead ? "failed" : "all"],
    enabled: agents.enabled,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) =>
      api.client.outbox.list({
        params: { co },
        query: {
          connection: c.id,
          limit: 50,
          ...(dead ? { group: "failed" as const } : {}),
          ...(pageParam ? { cursor: pageParam } : {}),
        },
      }),
    getNextPageParam: (p) => p.next_cursor ?? undefined,
  });
  const retry = useMutation({
    mutationFn: (id: string) => api.client.outbox.retry({ params: { co, id } }),
    onSuccess: () => toast.add({ title: "Queued again", type: "success" }),
    onError: (e) => toast.add({ title: problemText(e, "Not retried."), type: "error" }),
    onSettled: () => qc.invalidateQueries({ queryKey: outboxKey(co) }),
  });
  const cancel = useMutation({
    mutationFn: (id: string) => api.client.outbox.cancel({ params: { co, id }, body: {} }),
    onSuccess: () => toast.add({ title: "Cancelled", type: "success" }),
    onError: (e) => toast.add({ title: problemText(e, "Not cancelled."), type: "error" }),
    onSettled: () => qc.invalidateQueries({ queryKey: outboxKey(co) }),
  });
  if (!agents.enabled)
    return (
      <p className="text-sm text-muted-foreground" data-slot="acts-off">
        Agents are off: nothing acts through connections.
      </p>
    );
  if (q.isPending) return <Loading label={dead ? "Loading dead letters" : "Loading activity"} />;
  if (q.isError) return <LoadError what={dead ? "Dead letters" : "Activity"} error={q.error} />;
  const rows = q.data.pages.flatMap((p) => p.data);
  if (!rows.length)
    return (
      <EmptyState
        variant="compact"
        icon={SendIcon}
        title={dead ? "No dead letters" : "No activity"}
        description={dead ? "Nothing through it failed." : "Nothing has acted through it yet."}
      />
    );
  return (
    <div className="flex flex-col gap-2">
      <ul aria-label={dead ? "Dead letters" : "Activity"} className="flex flex-col">
        {rows.map((x) => {
          const summary = outboxSummary(x);
          return (
            <li
              key={x.id}
              data-status={x.status}
              className="group/row flex min-h-9 items-center gap-2 border-b border-border py-1.5 text-[13px]"
            >
              <Badge
                variant={
                  x.status === "dead" ? "danger" : x.status === "sent" ? "success" : "neutral"
                }
              >
                {x.status === "dead" ? "Dead letter" : x.status}
              </Badge>
              <span className="min-w-0 flex-1 truncate" title={summary}>
                {summary || "—"}
              </span>
              {x.dead_reason ? <InfoTip label={`Why: ${summary}`}>{x.dead_reason}</InfoTip> : null}
              <span className="shrink-0 tabular-nums text-muted-foreground">
                {formatDateTime(x.sent_at ?? x.created_at)}
              </span>
              {dead ? (
                <RowActions className="w-16">
                  <RowAction
                    icon={RotateCcwIcon}
                    label={`Retry ${summary}`}
                    tip="Retry"
                    disabled={retry.isPending}
                    onClick={() => retry.mutate(x.id)}
                  />
                  <RowAction
                    icon={BanIcon}
                    label={`Cancel ${summary}`}
                    tip="Cancel"
                    disabled={cancel.isPending}
                    onClick={() => cancel.mutate(x.id)}
                  />
                </RowActions>
              ) : null}
            </li>
          );
        })}
      </ul>
      <LoadMore
        what={dead ? "dead letters" : "activity"}
        hasMore={q.hasNextPage}
        loading={q.isFetchingNextPage}
        onMore={() => void q.fetchNextPage()}
      />
    </div>
  );
}
